import { omit } from 'lodash';

import i18n from '@/i18n';

import { initialState } from './initialState';
import { mapTourUIFromResponseToStore, mapTourDataFromResponseToStore } from './mappers';

const state = initialState;

// Suggest use getters instead of state
const getters = {
  getTitle (state) {
    return state.ui.title || i18n.t('defaults.platformTitle');
  },
  getBranded (state) {
    return state.ui.branded;
  },
  getSPA (state) { 
    return state.ui.spa;
  },
  getLayout (state) { 
    return state.ui.layout;
  },
  getMode (state) { 
    return state.ui.mode;
  },
  getId (state) { 
    return state.data.id;
  },
  getSettings (state) { 
    return state.data.settings;
  },
  getSlideshowConfig (state) { 
    return state.data.settings.slideshowConfig;
  },
  getPages (state) {
    return state.data.pages
      .filter(page => !state.ui.branded ? page.value !== 'contact' : page)
      .filter(page => page.visible)
      .map(page => {
        if (page.value === state.data.settings.topSection) {
          page.order = -1;
        }

        return page;
      })
      .sort((a, b) => a.order - b.order);
  },
  getEnabled (state) { 
    return state.data.settings.enabled;
  },
  getSold (state) { 
    return state.data.settings.sold;
  },
  getTopSection (state) {
    return state.data.settings.topSection;
  },
  getTopSectionPhotos (state) {
    return state.data.settings.topSectionPhotos;
  },
  getNumberOfPhotosOnIntro (state) {
    return state.data.settings.numberOfPhotosOnIntro;
  },
  getTopFirstPhoto (state) {
    return state.data.settings.topSection === 'none';
  },
  getTopVideo (state) {
    return state.data.settings.topSection === 'video';
  },
  getTop360Tour (state) {
    return state.data.settings.topSection === '360-tour';
  },
  getTopSlideshow (state) {
    return state.data.settings.topSection === 'slideshow';
  },
  getTopVimeoBackground (state) {
    return state.data.settings.topSection === 'vimeo-background';
  },
  getFullAddress (state) { 
    return state.data.fullAddress;
  },
  getAddress (state) { 
    return state.data.address;
  },
  getAgent (state) { 
    return state.data.agent;
  },
  getMultiAgents (state) { 
    return state.data.multiAgents;
  },
  getDetails (state) { 
    return state.data.details;
  },
  getParametersList (state) { 
    return omit(state.data.details, ['description']);
  },
  getVideo (state) { 
    return state.data.video;
  },
  getVideoSlideshow (state) { 
    return state.data.videoSlideshow;
  },
  getTour360 (state) {
    return state.data.tour360;
  },
  getAllPhotos (state) { 
    return state.data.photos;
  },
  getPhotoByIndex (state) {
    return index => state.data.photos[index];
  },
  getPhotosByLimit (state) {
    return limit => state.data.photos.slice(0, limit);
  },
  getFloorplanPhotos (state) { 
    return state.data.floorplanPhotos;
  },
  getFloorplanFiles (state) { 
    return state.data.floorplanFiles;
  },
  getFloorplanCubicasa (state) {
    return state.data.floorplanCubicasa;
  },
  getDocuments (state) { 
    return state.data.documents;
  },
  getMap (state) { 
    return state.data.map;
  },
  getEnableWalkScore (state) { 
    return state.data.walkScore.enableWalkScore;
  },
  getWalkScoreAddress (state) { 
    return state.data.walkScore.address;
  },
  getEnableHoodq (state) { 
    return state.data.walkScore.enableHoodq;
  },
  getHoodqUrl (state) { 
    return state.data.walkScore.hoodqUrl;
  },
  getFooter (state) { 
    return state.data.footer;
  },
  getUnpaid (state) {
    return state.data.payments.enabled && !state.data.payments.paid;
  },
  getPaymentLink (state) {
    return state.data.payments.paymentLink;
  },
  getMusic (state) { 
    return state.data.settings.music;
  },
  getExitPopup (state) { 
    return state.data.settings.exitPopup;
  },
  getLeadCapture (state) { 
    return state.data.settings.leadCapture;
  },
  getFavIcon (state) {
    return state.data.branding.favIcon;
  },
  getLogoEmail (state) {
    return state.data.branding.logoEmail;
  },
  getButtonColor (state) {
    return state.data.branding.customColorScheme ? state.data.branding.color.button : '';
  },
  getBackgroundColor (state) {
    return state.data.branding.customColorScheme ? state.data.branding.color.background : '';
  },
  getPrimaryColor (state) {
    return state.data.settings.layoutConfig.enabled ? state.data.settings.layoutConfig.primaryColor : '';
  },
  getOpenHouseEnabled (state) {
    return state.data.settings.openHouse.enabled;
  },
  getOpenHouseDays (state) {
    return state.data.settings.openHouse.days;
  },
  getAccess (state) {
    return state.data.settings.access;
  },
};

const mutations = {
  setTour (state, payload) {
    state.ui = Object.assign({}, state.ui, mapTourUIFromResponseToStore(payload, state.ui.branded));
    state.data = Object.assign({}, state.data, mapTourDataFromResponseToStore(payload, state.ui.branded));
  },
  setBrandedV2 (state, value) {
    state.ui.branded = value;
  },
};

const actions = {
  setTour ({commit}, payload) {
    commit('setTour', payload);
  },
  setBrandedV2 ({commit}, value) {
    commit('setBrandedV2', value);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
